/**
 * QuoteBackends — the Quote Database tab
 *
 * Spreadsheet-style list of every backend quote breakdown imported from the
 * Log Book (and, later, authored in ElecBid). Filter by quote #, client, or
 * job #; click a row for the full backend view (scope hours + materials by
 * package, sold package highlighted).
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4",
  amber: "#f59e0b", red: "#ef4444", blue: "#3b82f6",
};

function money(n) {
  if (n == null) return "—";
  return "$" + (+n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

async function api(params) {
  const qs = new URLSearchParams(params).toString();
  const res = await apiFetch(`/api/quote-backends?${qs}`);   // requireCrew-gated: sends the Supabase JWT
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

export default function QuoteBackends() {
  const [search,    setSearch]    = useState("");
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [detail,    setDetail]    = useState(null);   // { backend, lines }
  const [detailId,  setDetailId]  = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);

  const load = useCallback(async (s = "") => {
    setLoading(true);
    setError(null);
    try {
      const data = await api({ action: "list", search: s, limit: 300 });
      setRows(data.rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(search), 350);
    return () => clearTimeout(t);
  }, [search, load]);

  const openDetail = async (id) => {
    setDetailId(id);
    setDetail(null);
    try {
      const data = await api({ action: "detail", id });
      setDetail(data);
    } catch (e) {
      setDetail({ error: e.message });
    }
  };

  const runImport = async () => {
    setImporting(true);
    setImportMsg(null);
    try {
      const res = await apiFetch("/api/quote-backends", {   // requireAdmin-gated
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "import" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details || data.error || "Import failed");
      setImportMsg(`Imported ${data.imported} quotes (${data.lines} lines) from ${data.tabs?.length} tabs${data.errorCount ? ` — ${data.errorCount} errors` : ""}`);
      load(search);
    } catch (e) {
      setImportMsg(`Import failed: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px", color: C.text, fontFamily: "'Roboto', sans-serif" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: ".04em", color: C.lime }}>
            Quote Database
          </div>
          <div style={{ fontSize: 14, color: C.muted }}>
            Backend quote breakdowns — van hours, materials & permits at cost — imported from the Log Book
          </div>
        </div>
        <button
          onClick={runImport}
          disabled={importing}
          style={{
            background: "rgba(165,206,58,.12)", border: `1px solid rgba(165,206,58,.4)`,
            borderRadius: 10, color: C.lime, fontWeight: 700, fontSize: 13,
            padding: "10px 18px", cursor: "pointer", opacity: importing ? 0.6 : 1,
            fontFamily: "'Roboto', sans-serif",
          }}
        >
          {importing ? "Importing…" : "Import from Log Book"}
        </button>
      </div>

      {importMsg && (
        <div style={{
          background: importMsg.startsWith("Import failed") ? "rgba(239,68,68,.08)" : "rgba(165,206,58,.08)",
          border: `1px solid ${importMsg.startsWith("Import failed") ? "rgba(239,68,68,.3)" : "rgba(165,206,58,.3)"}`,
          borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13,
          color: importMsg.startsWith("Import failed") ? C.red : C.lime,
        }}>
          {importMsg}
        </div>
      )}

      {/* Search */}
      <input
        type="text"
        placeholder="Filter by quote #, client, or job # …"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: "100%", boxSizing: "border-box", background: C.surface,
          border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
          fontSize: 14, padding: "10px 14px", outline: "none", marginBottom: 14,
          fontFamily: "'Roboto', sans-serif",
        }}
        onFocus={e => e.target.style.borderColor = C.lime}
        onBlur={e => e.target.style.borderColor = C.border}
      />

      {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", background: "#11141a" }}>
                {["Quote", "Client", "Status", "Jobs", "Van Hrs", "Hours $", "Expenses $", "Total Sell", "Jobber Subtotal", "Pkgs", "Tab", ""].map((h, i) => (
                  <th key={i} style={{ padding: "9px 10px", fontWeight: 600, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} style={{ padding: 18, color: C.muted, textAlign: "center" }}>Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={12} style={{ padding: 18, color: C.muted, textAlign: "center" }}>
                  Nothing here yet — hit "Import from Log Book" (sheet must be shared with the service account).
                </td></tr>
              )}
              {!loading && rows.map(r => (
                <tr
                  key={r.id}
                  onClick={() => openDetail(r.id)}
                  style={{ borderBottom: "1px solid #1e2328", cursor: "pointer", background: detailId === r.id ? "rgba(165,206,58,.06)" : "none" }}
                >
                  <td style={{ padding: "7px 10px", color: C.lime, fontWeight: 700, whiteSpace: "nowrap" }}>#{r.quote_num}</td>
                  <td style={{ padding: "7px 10px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.client_name || "—"}</td>
                  <td style={{ padding: "7px 10px", color: C.muted, whiteSpace: "nowrap" }}>{r.jobber_status || "—"}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{r.job_nums || "—"}</td>
                  <td style={{ padding: "7px 10px" }}>{r.van_hours_quoted ?? "—"}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{money(r.hours_dollar_quoted)}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{money(r.expenses_cost_quoted)}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{money(r.total_sell)}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{money(r.jobber_subtotal)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "center" }}>
                    {r.package_count > 1
                      ? <span style={{ color: C.amber, fontWeight: 700 }}>{r.package_count}</span>
                      : r.package_count}
                  </td>
                  <td style={{ padding: "7px 10px", color: C.muted, fontSize: 11, whiteSpace: "nowrap" }}>{r.source_tab}</td>
                  <td style={{ padding: "7px 10px" }}>
                    {(r.warnings?.length > 0) && <span title={r.warnings.join("\n")} style={{ color: C.amber }}>⚠</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {detailId && (
        <div style={{ marginTop: 18, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 22px" }}>
          {!detail && <div style={{ color: C.muted, fontSize: 13 }}>Loading backend quote…</div>}
          {detail?.error && <div style={{ color: C.red, fontSize: 13 }}>{detail.error}</div>}
          {detail?.backend && <BackendDetail backend={detail.backend} lines={detail.lines} onClose={() => { setDetailId(null); setDetail(null); }} />}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;600;700&display=swap');
      `}</style>
    </div>
  );
}

function BackendDetail({ backend: b, lines, onClose }) {
  // Group lines by package, preserving order
  const packages = [];
  const byPkg = {};
  for (const l of lines) {
    if (!byPkg[l.package]) { byPkg[l.package] = []; packages.push(l.package); }
    byPkg[l.package].push(l);
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: ".04em", color: C.lime }}>
            Quote #{b.quote_num} — {b.client_name || "Unknown client"}
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
            {b.jobber_status || "no Jobber status"}
            {b.job_nums ? ` · Job(s) ${b.job_nums}` : " · no job yet"}
            {` · ${b.source_tab}`}
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, fontSize: 13, padding: "6px 14px", cursor: "pointer" }}>
          Close
        </button>
      </div>

      {/* Summary strip */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {[
          ["Van Hours", b.van_hours_quoted != null ? `${b.van_hours_quoted}h` : "—"],
          ["Hours $", money(b.hours_dollar_quoted)],
          ["Expenses (cost)", money(b.expenses_cost_quoted)],
          ["Expenses (sell)", money(b.expenses_sell_quoted)],
          ["Total Sell", money(b.total_sell)],
          ["Jobber Subtotal", money(b.jobber_subtotal)],
        ].map(([k, v]) => (
          <div key={k} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px" }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{k}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{v}</div>
          </div>
        ))}
      </div>

      {(b.warnings?.length > 0) && (
        <div style={{ background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 8, padding: "8px 14px", marginBottom: 14, color: C.amber, fontSize: 12 }}>
          {b.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}

      {/* Packages → sections → lines, Log Book style */}
      {packages.map(pkg => {
        const pkgLines = byPkg[pkg];
        const sold = b.sold_package && pkg === b.sold_package;
        return (
          <div key={pkg} style={{
            border: `1px solid ${sold ? "rgba(165,206,58,.5)" : C.border}`,
            borderRadius: 12, padding: "12px 16px", marginBottom: 12,
            background: sold ? "rgba(165,206,58,.04)" : "none",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: sold ? C.lime : C.text }}>
                {pkg === "MAIN" ? (packages.length > 1 ? "Main" : "Quote") : pkg}
              </div>
              {sold && (
                <span style={{ fontSize: 10, fontWeight: 700, color: "#0d0f12", background: C.lime, borderRadius: 4, padding: "2px 8px" }}>
                  SOLD — linked to job
                </span>
              )}
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <tbody>
                {pkgLines.map(l => l.is_header ? (
                  <tr key={l.id}>
                    <td colSpan={6} style={{ padding: "8px 6px 3px", color: C.muted, fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: ".05em" }}>
                      {l.description}
                    </td>
                  </tr>
                ) : (
                  <tr key={l.id} style={{ borderBottom: "1px solid #1a1e26" }}>
                    <td style={{ padding: "4px 6px", width: 50, textAlign: "right", color: l.is_labour ? C.lime : C.text, fontWeight: 600, whiteSpace: "nowrap" }}>
                      {l.qty ?? ""}
                    </td>
                    <td style={{ padding: "4px 6px", width: 36, color: C.muted }}>{l.unit ?? ""}</td>
                    <td style={{ padding: "4px 6px", color: C.text }}>{l.description}</td>
                    <td style={{ padding: "4px 6px", width: 90, textAlign: "right", color: C.muted, whiteSpace: "nowrap" }}>
                      {l.our_cost != null ? money(l.our_cost) : ""}
                    </td>
                    <td style={{ padding: "4px 6px", width: 90, textAlign: "right", whiteSpace: "nowrap" }}>
                      {l.client_total != null ? money(l.client_total) : ""}
                    </td>
                    <td style={{ padding: "4px 6px", width: 50, color: C.blue, fontSize: 11, whiteSpace: "nowrap" }}>
                      {l.job_col ? `→ ${l.job_col}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
